import type {StepCompletionAttemptADto} from '@cohort/admin-schemas/journeyParticipation';
import Button from '@cohort/merchants/components/buttons/Button';
import {StepCompletionsView} from '@cohort/merchants/components/campaigns/journeys/StepCompletionsView';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@cohort/merchants/components/modals/Dialog';
import {useTranslation} from 'react-i18next';

type StepCompletionModalProps = {
  onClose: () => void;
  title: string;
  isStepFailed: boolean;
  sortedStepCompletionAttempts: Array<StepCompletionAttemptADto>;
  successfulStepCompletionAttempt: StepCompletionAttemptADto | undefined;
};

const StepCompletionModal: React.FC<StepCompletionModalProps> = ({
  onClose,
  title,
  isStepFailed,
  sortedStepCompletionAttempts,
  successfulStepCompletionAttempt,
}) => {
  const {t} = useTranslation('pages', {
    keyPrefix: 'campaigns.campaign.activity.journey.journeyParticipationsStep',
  });

  if (!isStepFailed && successfulStepCompletionAttempt === undefined) {
    throw new Error("successfulStepCompletionAttempt can't be undefined if step is completed");
  }

  const isManuallyCompleted =
    successfulStepCompletionAttempt !== undefined && successfulStepCompletionAttempt.manual;
  const isSuccessfullyCompleted = successfulStepCompletionAttempt !== undefined;

  return (
    <Dialog onOpenChange={onClose} open>
      <DialogContent className="w-[600px]">
        <DialogHeader className="space-y-6">
          <DialogTitle className="text-lg font-semibold tracking-normal">{title}</DialogTitle>
        </DialogHeader>

        <DialogBody>
          {isManuallyCompleted && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
              {t('stepCompletionModal.stepManualyCompleted')}
            </div>
          )}
          {(isSuccessfullyCompleted || isStepFailed) && (
            <StepCompletionsView sortedStepCompletionAttempts={sortedStepCompletionAttempts} />
          )}
        </DialogBody>

        <DialogFooter className="flex justify-end bg-transparent">
          <Button onClick={onClose} variant="primary">
            {t('stepCompletionModal.buttonClose')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StepCompletionModal;
